/**
 * "MUÉSTRAME" — el Asistente que señala dónde tocar.
 *
 * Pedido de Esteban (13-sep): quien no sabe dónde está algo le pregunta al
 * Asistente, y el Asistente lo LLEVA: salta directo a la pantalla (con sus
 * filtros) y va iluminando, de a un paso, el botón, la pestaña o el campo que
 * hay que tocar, hasta terminar.
 *
 * Vive en `shared-types` por la misma razón que el mapa del sistema: el API
 * elige la receta y el panel la ejecuta; si cada lado tuviera su copia, el
 * Asistente ofrecería guías hacia botones que el panel ya no tiene.
 *
 * DOS CERROJOS (en el panel, `lib/guia.test.ts`):
 *  1. Cada ancla del catálogo existe en el código como `data-guia="…"`. Un
 *     botón que se renombra o se borra rompe el build, no la guía en vivo.
 *  2. Cada receta es válida: su primer paso dice a qué pantalla ir, todas sus
 *     anclas existen y viven en la pantalla del paso.
 *
 * El modelo NO inventa pasos: elige una receta de esta lista.
 */

import { MAPA_SISTEMA } from "./mapa-sistema";

export type QuienLaVe = "todos" | "admin" | "owner";

/** Un elemento de la pantalla que se puede señalar. */
export interface AnclaDeGuia {
  /** Se usa tal cual en `data-guia="…"`. */
  id: string;
  /** Pantalla donde vive. */
  ruta: string;
  /** Qué es, en palabras del negocio. */
  que: string;
}

/** Un paso de la guía. */
export interface PasoDeGuia {
  ancla: string;
  /** Lo que se le dice a la persona. Corto: una acción. */
  texto: string;
  /**
   * `al-tocar`: el paso avanza cuando la persona toca lo iluminado (un botón).
   * `con-siguiente`: la persona completa algo (escribir, elegir) y toca Siguiente.
   */
  avanza: "al-tocar" | "con-siguiente";
  /** Solo si el paso empieza en otra pantalla (siempre en el primero). */
  ruta?: string;
  /** Filtros de la pantalla, tal como viajan en la URL. */
  filtros?: Record<string, string | boolean>;
}

export interface RecetaDeGuia {
  id: string;
  /** Lo que se hace, como lo diría el dueño. */
  titulo: string;
  /** Preguntas en las palabras de la gente. Es lo que busca el Asistente. */
  responde: string[];
  quienLaVe: QuienLaVe;
  pasos: PasoDeGuia[];
}

export const ANCLAS_DE_GUIA: AnclaDeGuia[] = [
  // ── Mi negocio → Copiloto (tareas del cliente)
  { id: "installation.tab.copiloto", ruta: "/installation", que: "Pestaña «Copiloto»" },
  { id: "copiloto.como-lo-llama", ruta: "/installation", que: "Campo «Cómo lo llama a usted»" },
  { id: "copiloto.agregar-persona", ruta: "/installation", que: "Botón «Agregar persona» del asistente" },
  { id: "copiloto.frase", ruta: "/installation", que: "Campo «Frase para autorizar algo delicado»" },
  // ── Trabajos
  { id: "jobs.nuevo", ruta: "/jobs", que: "Botón «Nuevo trabajo»" },
  { id: "jobs.exportar", ruta: "/jobs", que: "Botón «Exportar a Excel»" },
  { id: "jobs.lista", ruta: "/jobs", que: "La lista de trabajos" },
  {
    id: "job-form.cliente",
    ruta: "/jobs",
    que: "Campo «Buscar cliente» del trabajo nuevo",
  },
  {
    id: "job-form.descripcion",
    ruta: "/jobs",
    que: "Campo «Descripción del trabajo»",
  },
  {
    id: "job-form.fecha",
    ruta: "/jobs",
    que: "Campo «Fecha y hora de la cita»",
  },
  {
    id: "job-form.tecnico",
    ruta: "/jobs",
    que: "Campo «Técnico asignado» del trabajo nuevo",
  },
  { id: "job-form.guardar", ruta: "/jobs", que: "Botón para crear el trabajo" },
  {
    id: "jobs.fila.editar",
    ruta: "/jobs",
    que: "Lápiz «Editar trabajo» de una fila",
  },
  {
    id: "job-editar.tecnico",
    ruta: "/jobs",
    que: "Campo «Técnico asignado» al editar",
  },
  {
    id: "job-editar.guardar",
    ruta: "/jobs",
    que: "Botón «Guardar» al editar un trabajo",
  },
  {
    id: "jobs.fila.cerrar-trabajo",
    ruta: "/jobs",
    que: "Botón «Cerrar trabajo» de un trabajo por validar",
  },
  {
    id: "validar.monto",
    ruta: "/jobs",
    que: "Campo «Monto cobrado» al cerrar",
  },
  {
    id: "validar.confirmar",
    ruta: "/jobs",
    que: "Botón que confirma el cierre",
  },
  {
    id: "jobs.fila.abrir-garantia",
    ruta: "/jobs",
    que: "Botón «Abrir garantía» de una instalación",
  },
  {
    id: "garantia.que-reporta",
    ruta: "/jobs",
    que: "Campo «¿Qué reporta el cliente?»",
  },
  {
    id: "garantia.guardar",
    ruta: "/jobs",
    que: "Botón «Abrir garantía» del formulario",
  },
  {
    id: "jobs.fila.cerrar-caso",
    ruta: "/jobs",
    que: "Botón «Cerrar caso» de un caso remoto",
  },
  { id: "remoto.que-se-hizo", ruta: "/jobs", que: "Campo «¿Qué se hizo?»" },
  {
    id: "remoto.confirmar",
    ruta: "/jobs",
    que: "Botón que cierra el caso remoto",
  },
  {
    id: "jobs.fila.mandar-tecnico",
    ruta: "/jobs",
    que: "Botón «Mandar técnico» de un caso remoto",
  },
  {
    id: "escalar.confirmar",
    ruta: "/jobs",
    que: "Botón «Mandar técnico» del formulario",
  },
  // ── Mi negocio (instalación)
  {
    id: "installation.guardar",
    ruta: "/installation",
    que: "Botón «Guardar cambios»",
  },
  {
    id: "installation.tab.mensajes",
    ruta: "/installation",
    que: "Pestaña «Mensajes»",
  },
  {
    id: "installation.tab.trabajos",
    ruta: "/installation",
    que: "Pestaña «Trabajos»",
  },
  {
    id: "mensajes.plantillas",
    ruta: "/installation",
    que: "Las tarjetas de los mensajes automáticos",
  },
  {
    id: "mensajes.a-quien",
    ruta: "/installation",
    que: "Sección «¿A quién le llegan los avisos internos?»",
  },
  // ── Ajustes técnicos
  { id: "settings.tab.horario", ruta: "/settings", que: "Pestaña «Horario»" },
  { id: "horario.activo", ruta: "/settings", que: "Casilla «Horario activo»" },
  { id: "horario.guardar", ruta: "/settings", que: "Botón «Guardar horario»" },
  {
    id: "settings.tab.clave-ia",
    ruta: "/settings",
    que: "Pestaña «Clave de IA»",
  },
  { id: "clave-ia.campo", ruta: "/settings", que: "Campo «API key»" },
  { id: "clave-ia.guardar", ruta: "/settings", que: "Botón «Guardar API key»" },
  // ── Empleados, canales, usuarios
  { id: "employees.nuevo", ruta: "/employees", que: "Botón «Nuevo empleado»" },
  {
    id: "employees.fila.editar",
    ruta: "/employees",
    que: "Lápiz para editar un empleado",
  },
  {
    id: "employee-form.telefono",
    ruta: "/employees",
    que: "Campo «Teléfono» del empleado",
  },
  {
    id: "employee-form.guardar",
    ruta: "/employees",
    que: "Botón que guarda el empleado",
  },
  { id: "channels.conectar", ruta: "/channels", que: "Botón «Conectar canal»" },
  { id: "users.invitar", ruta: "/users", que: "Botón «Invitar usuario»" },
  // ── Conversaciones
  {
    id: "conversations.lista",
    ruta: "/conversations",
    que: "La lista de conversaciones",
  },
  {
    id: "chat.tomar-control",
    ruta: "/conversations",
    que: "Botón «Tomar control» / «Devolver a AI»",
  },
  // ── Pedidos
  {
    id: "pedidos.escribir",
    ruta: "/pedidos",
    que: "El cuadro para contarle al asistente",
  },
  // ── Usuarios y Desempeño de técnicos (tanda "equipo")
  { id: "users.buscador", ruta: "/users", que: "Caja «Buscar por nombre, email o teléfono»" },
  { id: "desempeno.rango", ruta: "/desempeno", que: "Los chips de fechas de Desempeño" },
  { id: "desempeno.tabla", ruta: "/desempeno", que: "La tabla de garantías por técnico" },
  // ── Garantía suelta (artículo que no está en el sistema)
  {
    id: "jobs.abrir-garantia",
    ruta: "/jobs",
    que: "Botón «Abrir garantía» de la cabecera",
  },
  {
    id: "garantia.cliente",
    ruta: "/jobs",
    que: "Campo «Buscar cliente» de la garantía suelta",
  },
  // ── Menú «⋯» de la fila de Trabajos. Las acciones que antes eran botones de
  // solo ícono viven adentro: para llegar a «Editar trabajo» o a «Abrir
  // garantía» ahora hay que abrir este menú primero.
  {
    id: "jobs.fila.acciones",
    ruta: "/jobs",
    que: "Menú «⋯» de acciones de una fila",
  },
];

export const RECETAS_DE_GUIA: RecetaDeGuia[] = [
  {
    id: "copiloto-personas",
    titulo: "Decir quién le puede hablar al asistente",
    responde: [
      "¿Cómo agrego a alguien al asistente?",
      "¿Quién le puede dar órdenes al asistente?",
      "¿Cómo le doy acceso al asistente a alguien del equipo?",
    ],
    quienLaVe: "admin",
    pasos: [
      { ruta: "/installation", ancla: "installation.tab.copiloto", texto: "Toca la pestaña «Copiloto».", avanza: "al-tocar" },
      {
        ancla: "copiloto.agregar-persona",
        texto: "Toca «Agregar persona»: nombre, teléfono con código de país y si es dueño (ve todo) o equipo.",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "copiloto-como-lo-llama",
    titulo: "Cambiar cómo lo llama el asistente",
    responde: ["¿Cómo cambio cómo me dice el asistente?", "¿Cómo hago que el asistente me llame por mi nombre?"],
    quienLaVe: "admin",
    pasos: [
      { ruta: "/installation", ancla: "installation.tab.copiloto", texto: "Toca la pestaña «Copiloto».", avanza: "al-tocar" },
      { ancla: "copiloto.como-lo-llama", texto: "Escribe cómo quiere que lo llame (por ejemplo, su nombre).", avanza: "con-siguiente" },
      { ancla: "installation.guardar", texto: "Toca «Guardar cambios».", avanza: "al-tocar" },
    ],
  },
  {
    id: "copiloto-frase",
    titulo: "Elegir la frase para autorizar acciones delicadas",
    responde: [
      "¿Dónde pongo la frase de confirmación?",
      "¿Cómo cambio la frase del asistente?",
      "El asistente me pide una frase, ¿dónde se configura?",
    ],
    quienLaVe: "admin",
    pasos: [
      { ruta: "/installation", ancla: "installation.tab.copiloto", texto: "Toca la pestaña «Copiloto».", avanza: "al-tocar" },
      {
        ancla: "copiloto.frase",
        texto: "Escribe una frase de 3 o 4 palabras que nadie diría en un día normal.",
        avanza: "con-siguiente",
      },
      { ancla: "installation.guardar", texto: "Toca «Guardar cambios».", avanza: "al-tocar" },
    ],
  },
  {
    id: "crear-trabajo",
    titulo: "Crear un trabajo y asignarle técnico",
    responde: [
      "¿Cómo creo un trabajo?",
      "¿Cómo agendo una instalación?",
      "¿Dónde pongo una cita nueva?",
      "¿Cómo le asigno un técnico a un trabajo nuevo?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/jobs",
        ancla: "jobs.nuevo",
        texto: "Toca «Nuevo trabajo».",
        avanza: "al-tocar",
      },
      {
        ancla: "job-form.cliente",
        texto: "Busca y elige al cliente.",
        avanza: "con-siguiente",
      },
      {
        ancla: "job-form.descripcion",
        texto: "Escribe qué hay que hacer.",
        avanza: "con-siguiente",
      },
      {
        ancla: "job-form.fecha",
        texto: "Pon la fecha y la hora de la cita.",
        avanza: "con-siguiente",
      },
      {
        ancla: "job-form.tecnico",
        texto: "Elige el técnico.",
        avanza: "con-siguiente",
      },
      {
        ancla: "job-form.guardar",
        texto: "Toca aquí para crearlo.",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "reasignar-tecnico",
    titulo: "Cambiar el técnico de un trabajo",
    responde: [
      "¿Cómo cambio el técnico de un trabajo?",
      "¿Cómo reasigno un trabajo?",
      "¿Cómo le paso un trabajo a otro técnico?",
      "¿Cómo edito un trabajo?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/jobs",
        ancla: "jobs.fila.editar",
        texto: "Toca el lápiz del trabajo que quieres cambiar.",
        avanza: "al-tocar",
      },
      {
        ancla: "job-editar.tecnico",
        texto: "Elige el nuevo técnico.",
        avanza: "con-siguiente",
      },
      {
        ancla: "job-editar.guardar",
        texto: "Toca «Guardar».",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "validar-cierre",
    titulo: "Cerrar (validar) un trabajo que el técnico terminó",
    responde: [
      "¿Cómo cierro un trabajo?",
      "¿Cómo valido un trabajo terminado?",
      "¿Dónde pongo lo que se cobró?",
      "¿Cómo apruebo el cierre del técnico?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/jobs",
        filtros: { estado: "PENDIENTE_VALIDACION" },
        ancla: "jobs.fila.cerrar-trabajo",
        texto:
          "Estos son los que esperan tu validación. Toca «Cerrar trabajo» en el que quieras.",
        avanza: "al-tocar",
      },
      {
        ancla: "validar.monto",
        texto: "Pon el monto cobrado y revisa los datos.",
        avanza: "con-siguiente",
      },
      {
        ancla: "validar.confirmar",
        texto: "Toca aquí para cerrarlo.",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "abrir-garantia",
    titulo: "Abrir una garantía de una instalación",
    responde: [
      "¿Cómo abro una garantía?",
      "¿Qué hago si un artículo instalada falla?",
      "¿Dónde reporto una garantía?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/jobs",
        filtros: { estado: "COMPLETADO" },
        ancla: "jobs.fila.abrir-garantia",
        texto: "Busca la instalación y toca «Abrir garantía».",
        avanza: "al-tocar",
      },
      {
        ancla: "garantia.que-reporta",
        texto: "Escribe qué reporta el cliente y completa los datos.",
        avanza: "con-siguiente",
      },
      {
        ancla: "garantia.guardar",
        texto: "Toca «Abrir garantía».",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "cerrar-caso-remoto",
    titulo: "Cerrar un caso resuelto por teléfono",
    responde: [
      "¿Cómo cierro un caso que se resolvió por teléfono?",
      "¿Cómo cierro una garantía remota?",
      "¿Cómo cierro un caso sin mandar técnico?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/jobs",
        ancla: "jobs.fila.cerrar-caso",
        texto: "Toca «Cerrar caso» en el caso que se resolvió.",
        avanza: "al-tocar",
      },
      {
        ancla: "remoto.que-se-hizo",
        texto: "Escribe qué se hizo y elige nivel y causa.",
        avanza: "con-siguiente",
      },
      {
        ancla: "remoto.confirmar",
        texto: "Toca aquí para cerrarlo.",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "mandar-tecnico",
    titulo: "Mandar un técnico a un caso que no se resolvió por teléfono",
    responde: [
      "¿Cómo mando un técnico a un caso remoto?",
      "¿Cómo paso un caso a visita?",
      "No se pudo resolver por teléfono, ¿qué hago?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/jobs",
        ancla: "jobs.fila.mandar-tecnico",
        texto: "Toca «Mandar técnico» en el caso.",
        avanza: "al-tocar",
      },
      {
        ancla: "escalar.confirmar",
        texto: "Elige técnico y fecha, y toca «Mandar técnico».",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "ver-trabajos-manana",
    titulo: "Ver los trabajos de mañana",
    responde: [
      "¿Qué hay que instalar mañana?",
      "¿Qué trabajos hay mañana?",
      "¿Cuál es la agenda de mañana?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/jobs",
        filtros: { rango: "manana" },
        ancla: "jobs.lista",
        texto: "Estos son los trabajos de mañana.",
        avanza: "con-siguiente",
      },
    ],
  },
  {
    id: "ver-vencidos",
    titulo: "Ver los trabajos vencidos",
    responde: [
      "¿Qué trabajos están vencidos?",
      "¿Qué técnico se pasó de tiempo?",
      "¿Qué se atrasó?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/jobs",
        filtros: { vencidos: true },
        ancla: "jobs.lista",
        texto: "Estos son los trabajos que se pasaron de tiempo.",
        avanza: "con-siguiente",
      },
    ],
  },
  {
    id: "exportar-trabajos",
    titulo: "Sacar los trabajos a Excel",
    responde: [
      "¿Cómo saco los trabajos a Excel?",
      "¿Cómo exporto los trabajos?",
      "¿Cómo le mando los trabajos al contador?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/jobs",
        ancla: "jobs.exportar",
        texto:
          "Pon los filtros que quieras y toca «Exportar a Excel»: baja lo que ves.",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "cambiar-mensaje",
    titulo: "Cambiar el texto de un mensaje automático",
    responde: [
      "¿Cómo cambio el mensaje del recordatorio?",
      "¿Dónde edito la encuesta?",
      "¿Cómo cambio lo que le llega al cliente?",
      "¿Dónde se cambian los mensajes automáticos?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/installation",
        ancla: "installation.tab.mensajes",
        texto: "Toca la pestaña «Mensajes».",
        avanza: "al-tocar",
      },
      {
        ancla: "mensajes.plantillas",
        texto:
          "Busca la tarjeta del mensaje y edita el texto. Lo que va entre llaves lo llena el sistema.",
        avanza: "con-siguiente",
      },
      {
        ancla: "installation.guardar",
        texto: "Toca «Guardar cambios».",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "avisos-a-quien",
    titulo: "Elegir a quién le llegan los avisos (y encenderlos o apagarlos)",
    responde: [
      "¿Cómo cambio a quién le llegan los avisos?",
      "¿Cómo agrego un número para las alertas?",
      "¿Cómo apago un aviso?",
      "¿Cómo enciendo el aviso al técnico?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/installation",
        ancla: "installation.tab.mensajes",
        texto: "Toca la pestaña «Mensajes».",
        avanza: "al-tocar",
      },
      {
        ancla: "mensajes.a-quien",
        texto:
          "Aquí agregas los números y marcas a quién le llega cada aviso. El interruptor de cada tarjeta lo enciende o apaga.",
        avanza: "con-siguiente",
      },
      {
        ancla: "installation.guardar",
        texto: "Toca «Guardar cambios».",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "resumen-diario",
    titulo: "Cambiar la hora o el contenido del resumen de las 8 PM",
    responde: [
      "¿Cómo cambio la hora del resumen?",
      "¿Qué trae el resumen diario?",
      "¿Cómo apago el resumen de la noche?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/installation",
        ancla: "installation.tab.trabajos",
        texto: "Toca la pestaña «Trabajos» y baja hasta el resumen diario.",
        avanza: "al-tocar",
      },
      {
        ancla: "installation.guardar",
        texto: "Cuando lo ajustes, toca «Guardar cambios».",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "horario",
    titulo: "Cambiar el horario en que contesta la asesora",
    responde: [
      "¿Cómo cambio el horario de la asesora?",
      "¿A qué hora contesta el asistente?",
      "¿Cómo hago que conteste de noche?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/settings",
        ancla: "settings.tab.horario",
        texto: "Toca la pestaña «Horario».",
        avanza: "al-tocar",
      },
      {
        ancla: "horario.activo",
        texto: "Enciende o apaga el horario y ajusta las horas.",
        avanza: "con-siguiente",
      },
      {
        ancla: "horario.guardar",
        texto: "Toca «Guardar horario».",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "clave-ia",
    titulo: "Cargar la clave de IA",
    responde: [
      "¿Dónde pongo la clave de IA?",
      "¿Dónde cargo la API key?",
      "El asistente dice que no tiene clave, ¿qué hago?",
    ],
    quienLaVe: "owner",
    pasos: [
      {
        ruta: "/settings",
        ancla: "settings.tab.clave-ia",
        texto: "Toca la pestaña «Clave de IA».",
        avanza: "al-tocar",
      },
      {
        ancla: "clave-ia.campo",
        texto: "Pega la clave de Anthropic.",
        avanza: "con-siguiente",
      },
      {
        ancla: "clave-ia.guardar",
        texto: "Toca «Guardar API key».",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "nuevo-empleado",
    titulo: "Agregar un técnico o empleado",
    responde: [
      "¿Cómo agrego un técnico?",
      "¿Cómo creo un empleado?",
      "Entró un técnico nuevo, ¿dónde lo pongo?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/employees",
        ancla: "employees.nuevo",
        texto: "Toca «Nuevo empleado».",
        avanza: "al-tocar",
      },
      {
        ancla: "employee-form.telefono",
        texto: "Completa sus datos. El teléfono es el que recibe los avisos.",
        avanza: "con-siguiente",
      },
      {
        ancla: "employee-form.guardar",
        texto: "Toca aquí para guardarlo.",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "telefono-tecnico",
    titulo: "Cargar o cambiar el teléfono de un técnico",
    responde: [
      "¿Cómo le pongo el teléfono a un técnico?",
      "¿Por qué al técnico no le llegan los avisos?",
      "¿Cómo cambio el número de un empleado?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/employees",
        ancla: "employees.fila.editar",
        texto: "Toca el lápiz del técnico.",
        avanza: "al-tocar",
      },
      {
        ancla: "employee-form.telefono",
        texto: "Escribe su teléfono con el código de país.",
        avanza: "con-siguiente",
      },
      {
        ancla: "employee-form.guardar",
        texto: "Toca «Guardar cambios».",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "conectar-whatsapp",
    titulo: "Conectar el WhatsApp del negocio",
    responde: [
      "¿Cómo conecto WhatsApp?",
      "Se desconectó WhatsApp, ¿qué hago?",
      "¿Dónde escaneo el código QR?",
    ],
    quienLaVe: "todos",
    pasos: [
      {
        ruta: "/channels",
        ancla: "channels.conectar",
        texto:
          "Toca «Conectar canal» y escanea el QR desde el teléfono del negocio (WhatsApp → Dispositivos vinculados).",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "invitar-usuario",
    titulo: "Darle acceso al panel a alguien",
    responde: [
      "¿Cómo le doy acceso a alguien?",
      "¿Cómo invito a un usuario?",
      "¿Cómo creo una cuenta para la oficina?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/users",
        ancla: "users.invitar",
        texto: "Toca «Invitar usuario» y elige su rol.",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "tomar-control",
    titulo: "Contestar yo una conversación (que la asesora deje de responder)",
    responde: [
      "¿Cómo contesto yo a un cliente?",
      "¿Cómo hago que el asistente no responda en un chat?",
      "¿Cómo tomo el control de una conversación?",
      "¿Cómo le devuelvo el chat al asistente?",
    ],
    quienLaVe: "todos",
    pasos: [
      {
        ruta: "/conversations",
        ancla: "conversations.lista",
        texto: "Abre la conversación.",
        avanza: "al-tocar",
      },
      {
        ancla: "chat.tomar-control",
        texto:
          "Toca «Tomar control». Para devolvérsela al asistente, toca el mismo botón.",
        avanza: "al-tocar",
      },
    ],
  },
  {
    id: "reportar-problema",
    titulo: "Reportar un problema o pedir un cambio",
    responde: [
      "Algo no funciona, ¿a quién le digo?",
      "¿Cómo pido un cambio en el sistema?",
      "¿Dónde reporto un error?",
    ],
    quienLaVe: "todos",
    pasos: [
      {
        ruta: "/pedidos",
        ancla: "pedidos.escribir",
        texto:
          "Cuéntale al asistente qué pasó con tus palabras. Él te va preguntando lo que falta.",
        avanza: "con-siguiente",
      },
    ],
  },
  {
    id: "quien-deja-mas-garantias",
    titulo: "Ver qué técnico deja más garantías",
    responde: [
      "¿Cuál de mis técnicos deja más garantías?",
      "¿Qué técnico trabaja peor?",
      "¿Cómo veo el desempeño de los técnicos?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/desempeno",
        ancla: "desempeno.rango",
        texto: "Elegí el período que quieras mirar: esta semana, este mes o un rango tuyo.",
        avanza: "con-siguiente",
      },
      {
        ancla: "desempeno.tabla",
        texto:
          "Mirá la columna de garantías por cada 10 instalaciones: en rojo están los que más dejan. Tocá el título de una columna para ordenar por esa.",
        avanza: "con-siguiente",
      },
    ],
  },
  {
    id: "encontrar-usuario",
    titulo: "Encontrar a alguien en Usuarios",
    responde: [
      "¿Cómo busco a una persona en usuarios?",
      "¿Dónde veo quién está desactivado?",
    ],
    quienLaVe: "admin",
    pasos: [
      {
        ruta: "/users",
        ancla: "users.buscador",
        texto: "Escribí su nombre, su email o su teléfono. Los filtros de al lado acotan por rol y por si está activo.",
        avanza: "con-siguiente",
      },
    ],
  },
];

/**
 * Los errores del catálogo. Vacío = todo en orden. PURO.
 * Lo usa el test del panel, que además cruza las anclas contra el código.
 */
export function erroresDelCatalogoDeGuia(
  anclas: AnclaDeGuia[] = ANCLAS_DE_GUIA,
  recetas: RecetaDeGuia[] = RECETAS_DE_GUIA,
): string[] {
  const errores: string[] = [];
  const porId = new Map<string, AnclaDeGuia>();
  for (const a of anclas) {
    if (porId.has(a.id)) errores.push(`ancla repetida: ${a.id}`);
    porId.set(a.id, a);
    if (!MAPA_SISTEMA.some((p) => p.ruta === a.ruta)) {
      errores.push(
        `el ancla ${a.id} vive en ${a.ruta}, que no está en el mapa del sistema`,
      );
    }
  }
  const ids = new Set<string>();
  for (const r of recetas) {
    if (ids.has(r.id)) errores.push(`receta repetida: ${r.id}`);
    ids.add(r.id);
    if (r.pasos.length === 0) {
      errores.push(`la receta ${r.id} no tiene pasos`);
      continue;
    }
    if (!r.pasos[0]!.ruta)
      errores.push(
        `la receta ${r.id} no dice a qué pantalla ir en el primer paso`,
      );
    let rutaActual = r.pasos[0]!.ruta ?? "";
    for (const [i, paso] of r.pasos.entries()) {
      if (paso.ruta) rutaActual = paso.ruta;
      const ancla = porId.get(paso.ancla);
      if (!ancla) {
        errores.push(
          `la receta ${r.id}, paso ${i + 1}, usa el ancla inexistente ${paso.ancla}`,
        );
        continue;
      }
      if (ancla.ruta !== rutaActual) {
        errores.push(
          `la receta ${r.id}, paso ${i + 1}: el ancla ${paso.ancla} vive en ${ancla.ruta}, no en ${rutaActual}`,
        );
      }
      if (!paso.texto.trim())
        errores.push(
          `la receta ${r.id}, paso ${i + 1}, no le dice nada a la persona`,
        );
    }
  }
  return errores;
}

/** Quién puede ver una receta, según el rol de quien pregunta. PURO. */
export function recetaVisiblePara(
  receta: RecetaDeGuia,
  rol: string | undefined,
): boolean {
  if (receta.quienLaVe === "todos") return true;
  if (receta.quienLaVe === "owner") return rol === "OWNER";
  return rol === "OWNER" || rol === "ADMIN";
}

function normalizar(s: string): string {
  return (s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9 ]/g, " ");
}

/** Palabras que no distinguen una tarea de otra. */
const VACIAS = new Set([
  "como",
  "donde",
  "para",
  "que",
  "hago",
  "puedo",
  "esta",
  "este",
  "una",
  "uno",
  "los",
  "las",
  "del",
  "con",
  "por",
]);

/**
 * Las recetas que pueden responder una pregunta, de la más a la menos
 * probable. Compara por raíz de 5 letras, igual que el buscador de pantallas:
 * "reasigno" encuentra "reasignar". PURO.
 */
export function buscarRecetas(
  pregunta: string,
  rol?: string,
  max = 3,
): RecetaDeGuia[] {
  const palabras = normalizar(pregunta)
    .split(/\s+/)
    .filter((p) => p.length >= 4 && !VACIAS.has(p))
    .map((p) => p.slice(0, 5));
  if (palabras.length === 0) return [];
  return RECETAS_DE_GUIA.filter((r) => recetaVisiblePara(r, rol))
    .map((r) => {
      const responde = normalizar(r.responde.join(" "));
      const titulo = normalizar(r.titulo);
      let puntos = 0;
      for (const w of palabras) {
        if (responde.includes(w)) puntos += 2;
        if (titulo.includes(w)) puntos += 3;
      }
      return { r, puntos };
    })
    .filter((x) => x.puntos > 0)
    .sort((a, b) => b.puntos - a.puntos)
    .slice(0, max)
    .map((x) => x.r);
}
